import axiosInstance from "./axiosConfig";
import {
  Case,
  CreateCaseRequest,
  UpdateCaseRequest,
  USCISReceipt,
  AddUSCISReceiptRequest
} from "./types/case";
import { handleAPIError } from "./utils/errors";
import { VISAVALUE } from "../types/visa.type";
import { DataDocs } from "../types/tables-data";

const BASE_URL = "/cases";

export interface CaseStrategySummaryQuestion {
  id: string;
  questionText: string;
  description: string;
  isRequired: boolean;
  fieldType: string;
  options: any | null;
  answer: any;
  subOptions?: Record<string, string> | null;
  subVariableName?: string | null;
  subAnswer?: any;
}

export interface CaseStrategySummarySection {
  section: string;
  questions: CaseStrategySummaryQuestion[];
}

export interface CaseStrategySummaryResponse {
  individual_id: string;
  visa_type: string;
  sections: CaseStrategySummarySection[];
}

type AutoFillApplicantInfoWithDocumentsBody = {
  document_paths: string[];
  document_ids: string[];
  case_id: string;
  force_override?: boolean;
  enable_custom_upload_guidance?: boolean;
};

type PreselectedDocument = {
  category: string;
  document_type: string;
};

export type GetPreselectedDocumentsForOverviewResponse = Record<
  string,
  PreselectedDocument[]
>;

export const getCaseStrategySummary = async (
  individualId: string,
  visaType: string
): Promise<CaseStrategySummaryResponse> => {
  const response = await axiosInstance.get<CaseStrategySummaryResponse>(
    `/case-strategy/summary`,
    {
      params: {
        individual_id: individualId,
        visa_type: visaType
      }
    }
  );
  return response.data;
};

export const CasesAPI = {
  async list(): Promise<Case[]> {
    try {
      const response = await axiosInstance.get(BASE_URL);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getByIndividualId(individualId: string): Promise<Case> {
    try {
      const response = await axiosInstance.get(`${BASE_URL}`, {
        params: { individual_id: individualId }
      });
      const cases = response.data as Case[];
      if (!cases.length) {
        throw new Error("No case found for this individual");
      }
      return cases[0]; // Return the first case
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getByIndividualIds(individualIds: string[]): Promise<Case[]> {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/by-individuals`, {
        individual_ids: individualIds
      });
      return response.data as Case[];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async create(data: CreateCaseRequest): Promise<Case> {
    try {
      const response = await axiosInstance.post(BASE_URL, data);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async update(id: string, data: UpdateCaseRequest): Promise<Case> {
    try {
      const response = await axiosInstance.put(`${BASE_URL}/${id}`, data);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${BASE_URL}/${id}`);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getUSCISReceipts(caseId: string): Promise<USCISReceipt[]> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${caseId}/uscis-receipts`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async addUSCISReceipt(
    caseId: string,
    data: AddUSCISReceiptRequest
  ): Promise<USCISReceipt> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${caseId}/uscis-receipts`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getUSCISReceipt(
    caseId: string,
    receiptId: string
  ): Promise<USCISReceipt> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${caseId}/uscis-receipts/${receiptId}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteUSCISReceipt(caseId: string, receiptId: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `${BASE_URL}/${caseId}/uscis-receipts/${receiptId}`
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async downloadCaseStrategy(
    individualId: string,
    type: "pdf" | "docx"
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get("/case-strategy/download", {
        params: {
          individual_id: individualId,
          type
        },
        responseType: "blob"
      });
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getPreselectedDocumentsForOverview(
    visaType: VISAVALUE
  ): Promise<GetPreselectedDocumentsForOverviewResponse> {
    try {
      const response =
        await axiosInstance.get<GetPreselectedDocumentsForOverviewResponse>(
          "/cases/preselected_document_types_overview_page",
          {
            params: {
              visaType
            }
          }
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async autoFillApplicantInfoWithDocuments(
    documents: DataDocs[],
    case_id: string
  ): Promise<void> {
    try {
      const document_ids = documents.map((doc) => doc.id || "");
      const document_paths = documents.map((doc) => doc.docRef || "");
      const requestBody: AutoFillApplicantInfoWithDocumentsBody = {
        case_id,
        document_ids,
        document_paths
      };
      await axiosInstance.post<
        any,
        any,
        AutoFillApplicantInfoWithDocumentsBody
      >(`/cases/${case_id}/autofill-applicant-info`, requestBody);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
